import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, CheckCircle2, Trash2, RotateCcw, Pencil, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { ReminderDialog } from "@/components/ReminderDialog";
import { CategoryIcon } from "@/components/CategoryIcon";
import { api, errorMessage } from "@/lib/api";
import { countdown, shortDate, recurrenceLabel } from "@/lib/format";
import { leadLabel } from "@/lib/push";

const TABS = [["upcoming", "Próximos"], ["overdue", "Em atraso"], ["done", "Concluídos"]];

export default function Reminders() {
  const [tab, setTab] = useState("upcoming");
  const [items, setItems] = useState(null);
  const [counts, setCounts] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const [cur, over] = await Promise.all([api.get("/reminders", { params: { status: tab } }), api.get("/reminders", { params: { status: "overdue" } })]);
      setItems(cur.data);
      setCounts({ overdue: over.data.length });
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (r, status) => { await api.put(`/reminders/${r.id}`, { status }); toast.success(status === "done" ? "Lembrete concluído." : "Lembrete reaberto."); load(); };
  const remove = async (r) => { await api.delete(`/reminders/${r.id}`); toast.success("Lembrete excluído."); load(); };

  return (
    <div className="ga-fade-up px-5 pt-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">Lembretes</h1>
        <button onClick={() => { setEditing(null); setOpen(true); }} className="flex h-11 w-11 items-center justify-center rounded-full text-white" style={{ background: "#13C9A3" }} aria-label="Novo lembrete" data-testid="reminders-add-button"><Plus size={22} /></button>
      </div>
      <div className="ga-surface mt-5 flex rounded-2xl p-1" role="tablist">
        {TABS.map(([k, l]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} className="relative flex-1 rounded-xl py-2.5 text-sm font-medium transition-colors" style={tab === k ? { background: "var(--ga-card)", color: "#0A6373", boxShadow: "0 2px 8px rgba(0,0,0,.06)" } : { color: "var(--ga-muted)" }} data-testid={`reminders-tab-${k}`}>
            {l}{k === "overdue" && counts.overdue > 0 && <span className="ml-1.5 rounded-full bg-red-500 px-1.5 text-[10px] font-semibold text-white">{counts.overdue}</span>}
          </button>
        ))}
      </div>
      <div className="mt-4 space-y-2.5" data-testid="reminders-list">
        {items === null && [1, 2, 3].map((i) => <div key={i} className="ga-card h-[74px] animate-pulse" />)}
        {items?.length === 0 && <div className="ga-card p-8 text-center text-sm" data-testid="reminders-empty-state"><p className="font-medium">{tab === "overdue" ? "Nada em atraso. Tudo em dia!" : tab === "done" ? "Nenhum lembrete concluído ainda." : "Nenhum lembrete próximo."}</p></div>}
        {items?.map((r) => {
          const c = countdown(r.due_at);
          return (
            <div key={r.id} className="ga-card flex items-center gap-3 p-3" data-testid={`reminder-item-${r.id}`}>
              <CategoryIcon category={r.memory?.category || "Outros"} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold">{r.title}</div>
                <div className="text-xs font-medium" style={{ color: r.status === "done" ? "var(--ga-muted)" : c.tone === "late" ? "#E5484D" : "#13C9A3" }}>{r.status === "done" ? "Concluído" : c.label}</div>
                <div className="ga-muted text-[11px]">{shortDate(r.due_at)} {new Date(r.due_at).toTimeString().slice(0, 5)} • {recurrenceLabel(r.recurrence, r.custom_days)}{r.lead_minutes != null && <> • {leadLabel(r.lead_minutes)}</>}{r.memory && <> • <Link to={`/memorias/${r.memory.id}`} className="underline-offset-2 hover:underline">{r.memory.title}</Link></>}</div>
              </div>
              <div className="flex items-center gap-0.5">
                {r.status === "done" ? <button onClick={() => setStatus(r, "pending")} className="ga-muted p-2" aria-label="Reabrir" data-testid={`reminder-reopen-${r.id}`}><RotateCcw size={18} /></button> : <button onClick={() => setStatus(r, "done")} className="p-2" style={{ color: "#13C9A3" }} aria-label="Concluir" data-testid={`reminder-done-${r.id}`}><CheckCircle2 size={22} /></button>}
                <button onClick={() => { setEditing(r); setOpen(true); }} className="ga-muted p-2" aria-label="Editar" data-testid={`reminder-edit-${r.id}`}><Pencil size={16} /></button>
                <button onClick={() => remove(r)} className="p-2 text-red-400" aria-label="Excluir" data-testid={`reminder-delete-${r.id}`}><Trash2 size={16} /></button>
              </div>
            </div>
          );
        })}
      </div>
      <ReminderDialog open={open} onOpenChange={setOpen} reminder={editing} onSaved={load} />
    </div>
  );
}
